<template>
  <div class="menu-celular a2-tema md:hidden" role="dialog" aria-modal="true" :aria-label="tx.menu">
    <nav class="menu-itens">
      <a
        v-for="(item, i) in navItems"
        :key="item.href"
        :href="item.href"
        class="menu-item"
        :class="{ ativo: item.href === route.path }"
        :style="{ animationDelay: `${60 + i * 45}ms` }"
        @click.prevent="irPara(item.href)"
      >
        <span>{{ item.label }}</span>
        <ArrowUpRight class="menu-seta" aria-hidden="true" />
      </a>
    </nav>

    <div v-if="visualNovo" class="menu-preferencias">
      <span class="menu-rotulo">{{ tx.preferencias }}</span>
      <div class="menu-controles">
        <LanguageSelector />
        <ThemeToggle />
      </div>
    </div>

    <div class="menu-acoes">
      <a
        :href="whatsappUrl"
        target="_blank"
        rel="noopener"
        class="menu-whatsapp"
        @click="$emit('close')"
      >
        <IconeWhatsApp />
        {{ tx.whatsapp }}
      </a>
      <div class="menu-secundarias">
        <a
          v-if="showSignUpButton"
          :href="loginUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="menu-secundaria"
          @click="$emit('close')"
        >
          {{ tx.login }}
        </a>
        <button
          v-if="showComecarAgora"
          type="button"
          class="menu-secundaria menu-comecar"
          @click="scrollToPricing(); $emit('close')"
        >
          {{ tx.comecar }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowUpRight } from 'lucide-vue-next'
import { useTextos, ROTAS_VISUAL_NOVO } from '../../i18n/idioma'
import LanguageSelector from './LanguageSelector.vue'
import ThemeToggle from './ThemeToggle.vue'
import IconeWhatsApp from '../a2/IconeWhatsApp.vue'

const textos = {
  pt: { beneficios: 'Benefícios', agentes: 'Agentes', precos: 'Preços', whitelabel: 'Whitelabel', login: 'Login', comecar: 'Começar Agora', menu: 'Menu', preferencias: 'Idioma e tema', whatsapp: 'Falar no WhatsApp', msg: 'Ola! Gostaria de saber mais sobre os agentes de IA da AutoVolt.' },
  en: { beneficios: 'Benefits', agentes: 'Agents', precos: 'Pricing', whitelabel: 'White Label', login: 'Log in', comecar: 'Get Started', menu: 'Menu', preferencias: 'Language and theme', whatsapp: 'Talk on WhatsApp', msg: 'Hi! I would like to know more about AutoVolt AI agents.' },
  es: { beneficios: 'Beneficios', agentes: 'Agentes', precos: 'Precios', whitelabel: 'White Label', login: 'Iniciar sesión', comecar: 'Empezar Ahora', menu: 'Menú', preferencias: 'Idioma y tema', whatsapp: 'Hablar por WhatsApp', msg: '¡Hola! Me gustaría saber más sobre los agentes de IA de AutoVolt.' },
}
const tx = useTextos(textos)
const route = useRoute()
const visualNovo = computed(() => ROTAS_VISUAL_NOVO.includes(route.path))
const whatsappUrl = computed(() => `https://api.whatsapp.com/send/?phone=5547988489706&text=${encodeURIComponent(tx.value.msg)}`)

// Trava a rolagem da página enquanto o menu está aberto
onMounted(() => { document.body.style.overflow = 'hidden' })
onUnmounted(() => { document.body.style.overflow = '' })

const navItems = computed(() => [
  { href: '#benefits', label: tx.value.beneficios },
  { href: '#agents', label: tx.value.agentes },
  { href: '#pricing', label: tx.value.precos },
  { href: '/whitelabel', label: tx.value.whitelabel },
  { href: '/a2', label: 'A2' }
])

const emit = defineEmits<{
  close: []
}>()

import { computed } from 'vue';
import { useRouter } from 'vue-router'

const router = useRouter();

// Função para capturar UTMs da URL atual
const getUtmParams = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = new URLSearchParams();
  
  // Lista de parâmetros UTM comuns
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  // Também captura o parâmetro 'ref' se existir
  const refParam = urlParams.get('ref');
  if (refParam) {
    utmParams.set('ref', refParam);
  }

  utmKeys.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      utmParams.set(key, value);
    }
  });
  
  return utmParams.toString();
};

// Função para adicionar UTMs a uma URL
const addUtmsToUrl = (baseUrl: string): string => {
  const utmString = getUtmParams();
  if (!utmString) return baseUrl;
  
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${utmString}`;
};

const showSignUpButton = computed(() => {
  return !['imobiliarias', 'clinicas'].includes(router.currentRoute.value.name as string);
});

// Na página do White Label os planos não têm botão até o cadastro da agência abrir: "Começar Agora" levaria a um beco
const showComecarAgora = computed(() => showSignUpButton.value && router.currentRoute.value.name !== 'whitelabel');

const scrollToPricing = () => {
  const pricingSection = document.getElementById('pricing')
  if (pricingSection) {
    pricingSection.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    })
  } else {
    router.push('/');
    setTimeout(() => {
        const element = document.getElementById('pricing');
        if (element) element.scrollIntoView({ behavior: 'smooth' })
      }, 400);
  }
}

const loginUrl = addUtmsToUrl('https://app.autovolt.ai/login')

// Âncoras da home funcionam de qualquer página: volta para a home e rola até a seção
const irPara = (href: string) => {
  emit('close')
  if (!href.startsWith('#')) {
    router.push(href).then(() => window.scrollTo(0, 0))
    return
  }
  const rolar = () => document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' })
  if (route.path === '/') rolar()
  else router.push('/').then(() => setTimeout(rolar, 400))
}
</script>

<style scoped>
.menu-celular {
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 49;
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding: 20px 20px calc(24px + env(safe-area-inset-bottom));
  overflow-y: auto;
  background: var(--a2-bg);
  border-top: 1px solid var(--a2-line);
  color: var(--a2-ink);
}

.menu-itens { display: flex; flex-direction: column; }

.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 4px;
  border-bottom: 1px solid var(--a2-line);
  font-family: var(--a2-display);
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--a2-ink);
  opacity: 0;
  transform: translateY(10px);
  animation: entrar 0.45s cubic-bezier(0.2, 0.7, 0.2, 1) forwards;
}

.menu-item.ativo { color: var(--a2-signal); }

.menu-seta { width: 22px; height: 22px; color: var(--a2-faint); transition: transform 0.2s ease, color 0.2s ease; }
.menu-item:active .menu-seta { transform: translate(2px, -2px); color: var(--a2-signal); }

@keyframes entrar {
  to { opacity: 1; transform: none; }
}

.menu-preferencias {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid var(--a2-line);
  background: var(--a2-card);
}

.menu-rotulo {
  font-family: var(--a2-mono);
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--a2-muted);
}

.menu-controles { display: flex; align-items: center; gap: 10px; }

.menu-acoes { margin-top: auto; display: flex; flex-direction: column; gap: 12px; }

.menu-whatsapp {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 17px 20px;
  border-radius: 16px;
  font-weight: 700;
  font-size: 16px;
  background: var(--a2-signal);
  color: var(--a2-signal-ink);
  box-shadow: 0 16px 40px -16px rgba(61, 255, 168, 0.6);
}

.menu-secundarias { display: grid; grid-template-columns: repeat(auto-fit, minmax(0, 1fr)); gap: 10px; }

.menu-secundaria {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid var(--a2-line-strong);
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  color: var(--a2-ink);
  background: transparent;
}

.menu-item:focus-visible, .menu-whatsapp:focus-visible, .menu-secundaria:focus-visible {
  outline: 2px solid var(--a2-primary-soft);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .menu-item { animation: none; opacity: 1; transform: none; }
}
</style>
